import React from 'react';
import { FileX, AlertTriangle, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';

export const VoidInvoiceDialog = ({
  open, onOpenChange, voidFromBulkAction, count, selectedInvoices, selectedInvoice,
  voidCategories, voidCategory, setVoidCategory, voidReason, setVoidReason,
  partialVoid, setPartialVoid, selectedItems, setSelectedItems,
  estimatedCost, setEstimatedCost, voidNotes, setVoidNotes, processing, onConfirm, onBack,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="void-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileX className="h-5 w-5 text-red-600" />
          Void {voidFromBulkAction ? `${count} Invoice${count > 1 ? 's' : ''}` : selectedInvoice?.invoice_number || 'Invoice'}
        </DialogTitle>
        <DialogDescription>
          Voiding creates a credit memo for audit trail. Use for legitimate transactions that need to be reversed.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        {(selectedInvoices.some(inv => inv.status === 'paid') || selectedInvoice?.status === 'paid') && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Refund Required</p>
                <p className="text-amber-700">One or more invoices are already paid. A refund will be flagged for processing.</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <Label>Void Category *</Label>
          <Select value={voidCategory} onValueChange={setVoidCategory}>
            <SelectTrigger className="mt-1" data-testid="void-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {voidCategories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Void Reason * <span className="text-xs text-gray-400">(min 10 characters)</span></Label>
          <Textarea
            placeholder="Provide detailed reason for voiding this invoice..."
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            className="mt-1"
            data-testid="void-reason-input"
          />
        </div>

        {!voidFromBulkAction && selectedInvoice?.items?.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox id="partial-void" checked={partialVoid} onCheckedChange={setPartialVoid} data-testid="partial-void-checkbox" />
              <Label htmlFor="partial-void" className="text-sm cursor-pointer">Partial void (select specific items)</Label>
            </div>
            {partialVoid && (
              <div className="ml-6 p-3 bg-gray-50 rounded-lg space-y-2 max-h-40 overflow-y-auto">
                {selectedInvoice.items.map((item, i) => {
                  const itemId = item.product_id || item.id || `item-${i}`;
                  return (
                    <div key={itemId} className="flex items-center gap-2">
                      <Checkbox
                        id={`item-${itemId}`}
                        checked={selectedItems.has(itemId)}
                        onCheckedChange={(checked) => {
                          const newSelected = new Set(selectedItems);
                          if (checked) newSelected.add(itemId);
                          else newSelected.delete(itemId);
                          setSelectedItems(newSelected);
                        }}
                      />
                      <Label htmlFor={`item-${itemId}`} className="text-sm cursor-pointer flex-1">
                        {item.product_name || item.name} ({item.quantity}) - ${(item.subtotal || 0).toFixed(2)}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div>
          <Label>Est. Production Cost (optional)</Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={estimatedCost}
              onChange={(e) => setEstimatedCost(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div>
          <Label>Additional Notes (optional)</Label>
          <Textarea
            placeholder="Additional notes..."
            rows={2}
            value={voidNotes}
            onChange={(e) => setVoidNotes(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onBack}>Back</Button>
          <Button
            className="flex-1 bg-red-600 hover:bg-red-700"
            onClick={onConfirm}
            disabled={processing || !voidCategory || !voidReason || voidReason.length < 10}
            data-testid="confirm-void-btn"
          >
            {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileX className="h-4 w-4 mr-2" />}
            {processing ? 'Voiding...' : 'Void & Create Credit Memo'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
